import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WifiOff as OfflineIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CloudOff as SyncErrorIcon,
} from '@mui/icons-material';

export interface CalendarError {
  type: 'network' | 'sync' | 'data' | 'permission' | 'rate_limit' | 'unknown';
  message: string;
  details?: string;
  code?: string;
  retryable?: boolean;
  retryAfter?: number; // seconds
  context?: Record<string, any>;
}

interface CalendarErrorDisplayProps {
  error: CalendarError | Error | null;
  onRetry?: () => void;
  onDismiss?: () => void;
  showDetails?: boolean;
  compact?: boolean;
  retryCount?: number;
  maxRetries?: number;
}

const getErrorIcon = (type: CalendarError['type']) => {
  switch (type) {
    case 'network':
      return <OfflineIcon />;
    case 'sync':
      return <SyncErrorIcon />;
    case 'rate_limit':
      return <WarningIcon />;
    case 'permission':
      return <ErrorIcon />;
    default:
      return <ErrorIcon />;
  }
};

const getErrorSeverity = (type: CalendarError['type']): 'error' | 'warning' | 'info' => {
  switch (type) {
    case 'rate_limit':
    case 'sync':
      return 'warning';
    case 'network':
      return 'info';
    default:
      return 'error';
  }
};

const parseError = (error: CalendarError | Error): CalendarError => {
  if ('type' in error) {
    return error;
  }

  // Parse standard Error objects
  const message = error.message || 'An unexpected error occurred';
  
  if (message.toLowerCase().includes('network') || message.toLowerCase().includes('fetch')) {
    return {
      type: 'network',
      message: 'Network connection failed',
      details: 'Unable to connect to the server. Please check your internet connection.',
      retryable: true,
    };
  }

  if (message.toLowerCase().includes('sync')) {
    return {
      type: 'sync',
      message: 'Calendar sync failed',
      details: message,
      retryable: true,
    };
  }

  if (message.toLowerCase().includes('rate') || message.toLowerCase().includes('limit')) {
    return {
      type: 'rate_limit',
      message: 'Too many requests',
      details: 'Please wait a moment before trying again.',
      retryable: true,
      retryAfter: 60,
    };
  }

  if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('unauthorized')) {
    return {
      type: 'permission',
      message: 'Access denied',
      details: 'You do not have permission to access this resource.',
      retryable: false,
    };
  }

  return {
    type: 'unknown',
    message,
    details: 'Please try again or contact support if the problem persists.',
    retryable: true,
  };
};

export const CalendarErrorDisplay: React.FC<CalendarErrorDisplayProps> = ({
  error,
  onRetry,
  onDismiss,
  showDetails: showDetailsProp = false,
  compact = false,
  retryCount = 0,
  maxRetries = 3,
}) => {
  const [showDetails, setShowDetails] = useState(showDetailsProp);

  if (!error) return null;

  const parsedError = parseError(error);
  const canRetry = parsedError.retryable && retryCount < maxRetries && onRetry;
  const severity = getErrorSeverity(parsedError.type);
  const icon = getErrorIcon(parsedError.type);

  const getTitle = () => {
    switch (parsedError.type) {
      case 'network':
        return 'Connection Problem';
      case 'sync':
        return 'Sync Error';
      case 'data':
        return 'Data Error';
      case 'permission':
        return 'Access Denied';
      case 'rate_limit':
        return 'Rate Limited';
      default:
        return 'Error';
    }
  };

  const getSuggestedActions = () => {
    switch (parsedError.type) {
      case 'network':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Contact your network administrator if the problem persists',
        ];
      case 'sync':
        return [
          'Check your calendar sync settings',
          'Verify your external calendar URLs are correct',
          'Try manually triggering a sync',
        ];
      case 'rate_limit':
        return [
          `Wait ${parsedError.retryAfter || 60} seconds before trying again`,
          'Reduce the frequency of your requests',
          'Contact support about rate limit increases',
        ];
      case 'permission':
        return [
          'Log out and log back in',
          'Contact your administrator for access',
          'Verify your account permissions',
        ];
      default:
        return [
          'Try refreshing the page',
          'Clear your browser cache',
          'Contact support if the problem persists',
        ];
    }
  };

  if (compact) {
    return (
      <Alert 
        severity={severity} 
        icon={icon}
        action={
          <Stack direction="row" spacing={1}>
            {canRetry && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
            {onDismiss && (
              <IconButton size="small" onClick={onDismiss}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        }
      >
        <AlertTitle>{getTitle()}</AlertTitle>
        {parsedError.message}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Alert 
          severity={severity} 
          icon={icon}
          action={
            onDismiss && (
              <IconButton size="small" onClick={onDismiss}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }
        >
          <AlertTitle>{getTitle()}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {parsedError.message}
          </Typography>

          {parsedError.details && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {parsedError.details}
            </Typography>
          )}

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {canRetry && (
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
              >
                Retry ({maxRetries - retryCount} left)
              </Button>
            )}

            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>

            <Button
              variant="text"
              size="small"
              startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Hide' : 'Show'} Details
            </Button>
          </Stack>

          {parsedError.retryAfter && (
            <Chip
              label={`Retry available in ${parsedError.retryAfter}s`}
              size="small"
              color="warning"
              sx={{ mb: 1 }}
            />
          )}
        </Alert>

        <Collapse in={showDetails}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Suggested Actions:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              {getSuggestedActions().map((action, index) => (
                <Typography key={index} component="li" variant="body2">
                  {action}
                </Typography>
              ))}
            </Box>

            {parsedError.code && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Error Code:</strong> {parsedError.code}
              </Typography>
            )}

            {parsedError.context && Object.keys(parsedError.context).length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Technical Details:
                </Typography>
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography 
                      variant="body2" 
                      component="pre" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(parsedError.context, null, 2)}
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default CalendarErrorDisplay;